import {getSiteUrl} from "@/helpers/functions";
import {coerceImageObject} from "@/helpers/imageObject";
import type {ItemListItem} from "@/components/SeoSchema/mode/SeoSchemaItemList";

export interface CollectionList {
    name: string;
    items: ItemListItem[];
}

export interface SeoSchemaCollectionPageProps {
    name: string;
    url: string;
    lists: CollectionList[];
    description?: string;
}

const toAbsoluteUrl = (siteUrl: string, url: string): string => {
    if (/^https?:\/\//i.test(url)) return url;
    const path = url.startsWith("/") ? url : `/${url}`;
    return `${siteUrl}${path}`;
};

export function SeoSchemaCollectionPage({name, url, lists, description}: SeoSchemaCollectionPageProps) {
    const populated = lists.filter(list => list.items.length > 0);
    if (populated.length === 0) return null;

    const siteUrl = getSiteUrl();
    const absUrl = toAbsoluteUrl(siteUrl, url);

    const nestedLists = populated.map((list, listIdx) => ({
        "@type": "ItemList",
        "position": listIdx + 1,
        "name": list.name,
        "itemListElement": list.items.map((item, idx) => {
            const entry: Record<string, unknown> = {
                "@type": "ListItem",
                "position": idx + 1,
                "name": item.name,
                "url": toAbsoluteUrl(siteUrl, item.url),
            };
            if (item.image) entry.image = coerceImageObject(item.image);
            return entry;
        }),
    }));

    const schema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": name,
        "url": absUrl,
        "mainEntity": {
            "@type": "ItemList",
            "itemListElement": nestedLists,
        },
    };

    if (description) schema.description = description;

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
        />
    );
}
